import { useState } from 'react';
import { IceCream, Search, User, Phone, Mail, Gift, Check, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { customerAPI } from '@/services/api';
import { Link } from 'react-router-dom';

const EmployeeCheckin = () => {
  const [identifier, setIdentifier] = useState('');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkinDone, setCheckinDone] = useState(false);

  const handleSearch = async () => {
    if (!identifier.trim()) {
      toast.error('Ingresa teléfono o correo del cliente');
      return;
    }

    setLoading(true);
    setCustomer(null);
    setCheckinDone(false);
    
    try {
      const response = await customerAPI.lookup(identifier.trim());
      setCustomer(response.data);
      toast.success(`Cliente encontrado: ${response.data.name}`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Cliente no encontrado. Debe registrarse primero en la app.');
      } else {
        toast.error('Error al buscar cliente');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async () => {
    if (!customer) return;

    setLoading(true);
    try {
      const response = await customerAPI.checkin(customer.email || customer.phone);
      setCustomer(response.data.customer);
      setCheckinDone(true);
      
      if (response.data.reward_unlocked) {
        toast.success('🎉 ¡El cliente desbloqueó una recompensa!', { duration: 5000 });
      } else {
        toast.success('✓ Visita registrada correctamente');
      }
    } catch (error) {
      toast.error('Error al registrar visita');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setIdentifier('');
    setCustomer(null);
    setCheckinDone(false);
  };

  const visitsProgress = customer ? customer.visits % 10 : 0;
  const canRedeemReward = customer && customer.visits >= 10;

  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-fixed"
      style={{ backgroundImage: 'url(https://hosting.photobucket.com/d970c25b-dfba-4164-a867-030817bbaa89/a5ee733e-d4af-4772-9a72-fb55c6e5b7f7.png)' }}
    >
      {/* Header */}
      <header className="sticky top-0 z-40 shadow-lg" style={{ backgroundColor: '#FFA6A6' }}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-center relative">
          <Link to="/" className="absolute left-4">
            <ArrowLeft className="w-6 h-6 text-white" />
          </Link>
          <div className="text-center">
            <h1 className="font-nunito font-bold text-white text-lg">Registro de Visitas</h1>
            <p className="text-white/80 text-sm">Panel Empleados</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Search Card */}
        <Card className="mb-6 rounded-3xl overflow-hidden bg-white shadow-lg" style={{ border: '5px solid #FFA6A6' }}>
          <CardContent className="p-6">
            <h2 className="font-nunito font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-rose-pop" />
              Buscar Cliente
            </h2>
            
            <div className="space-y-4">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Teléfono o correo del cliente"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-12 py-5 rounded-2xl border-2 border-rose-soft focus:border-rose-pop text-lg"
                  data-testid="employee-search-input"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  {identifier.includes('@') ? (
                    <Mail className="w-5 h-5 text-rose-pop" />
                  ) : (
                    <Phone className="w-5 h-5 text-rose-pop" />
                  )}
                </div>
              </div>
              
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="w-full bg-rose-pop hover:bg-rose-dark text-white font-bold py-5 rounded-2xl"
                data-testid="employee-search-btn"
              >
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Customer Info */}
        {customer && (
          <Card className="mb-6 rounded-3xl overflow-hidden bg-white shadow-lg" style={{ border: '5px solid #FFA6A6' }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-pop to-rose-soft flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-nunito font-bold text-xl text-gray-900">{customer.name}</h3>
                  <p className="text-gray-500">{customer.phone || customer.email}</p>
                </div>
              </div>

              {/* Progress */}
              <div className="bg-rose-faint rounded-2xl p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600 font-quicksand">Progreso actual</span>
                  <span className="font-nunito font-bold text-2xl text-rose-pop">{visitsProgress}/10</span>
                </div>
                
                {/* Progress dots */}
                <div className="flex justify-between gap-1">
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={i}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                        i < visitsProgress 
                          ? 'bg-rose-pop text-white' 
                          : 'bg-white border-2 border-rose-soft text-rose-soft'
                      }`}
                    >
                      {i < visitsProgress ? '🍦' : i + 1}
                    </div>
                  ))}
                </div>
              </div>

              {/* Reward Alert */}
              {canRedeemReward && (
                <div className="bg-mint/20 border-2 border-mint rounded-2xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <Gift className="w-8 h-8 text-mint" />
                    <div>
                      <p className="font-nunito font-bold text-gray-900">¡Recompensa disponible!</p>
                      <p className="text-sm text-gray-600">El cliente tiene {customer.visits} visitas</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {!checkinDone ? (
                <Button
                  onClick={handleCheckin}
                  disabled={loading}
                  className="w-full bg-mint hover:bg-mint/90 text-gray-900 font-bold py-6 rounded-2xl text-lg"
                  data-testid="register-visit-btn"
                >
                  <IceCream className="w-6 h-6 mr-2" />
                  {loading ? 'Registrando...' : 'Registrar Visita'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="bg-green-100 border-2 border-green-500 rounded-2xl p-4 flex items-center justify-center gap-2">
                    <Check className="w-6 h-6 text-green-600" />
                    <span className="font-bold text-green-700">Visita Registrada</span>
                  </div>
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    className="w-full border-rose-soft hover:bg-rose-faint rounded-2xl py-4"
                    data-testid="new-checkin-btn"
                  >
                    Registrar otro cliente
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="rounded-3xl overflow-hidden bg-white/90 shadow-lg">
          <CardContent className="p-5">
            <h3 className="font-nunito font-bold text-gray-900 mb-2">Instrucciones</h3>
            <ol className="text-sm text-gray-600 space-y-2 font-quicksand">
              <li>1. Pide al cliente su teléfono o correo</li>
              <li>2. Búscalo en el sistema</li>
              <li>3. Verifica que sea el cliente correcto</li>
              <li>4. Registra la visita</li>
              <li>5. Si tiene recompensa, aplícala en caja</li>
            </ol>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EmployeeCheckin;
